import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

/**
 * TableSkeleton - Skeleton loader for data tables
 *
 * Usage:
 *   <TableSkeleton rows={5} columns={4} />
 */
export function TableSkeleton({ rows = 5, columns = 4, showHeader = true }) {
  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        {showHeader && (
          <TableHead>
            <TableRow>
              {Array.from({ length: columns }).map((_, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width="80%" height={24} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton
                    variant="text"
                    width={colIndex === 0 ? '60%' : '80%'}
                    height={20}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * CardSkeleton - Skeleton loader for stat cards
 *
 * Usage:
 *   <CardSkeleton />
 */
export function CardSkeleton({ height = 120 }) {
  return (
    <Card sx={{ height }}>
      <CardContent>
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={36} />
        <Skeleton variant="text" width="30%" height={16} sx={{ mt: 1 }} />
      </CardContent>
    </Card>
  );
}

/**
 * DashboardSkeleton - Full dashboard skeleton with cards and table
 *
 * Usage:
 *   if (loading) return <DashboardSkeleton />;
 */
export function DashboardSkeleton({ cardCount = 4, tableRows = 5 }) {
  return (
    <Box sx={{ p: 2 }}>
      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Array.from({ length: cardCount }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <CardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Action buttons skeleton */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Skeleton variant="rounded" width={120} height={36} />
        <Skeleton variant="rounded" width={100} height={36} />
      </Box>

      {/* Table skeleton */}
      <TableSkeleton rows={tableRows} columns={5} />
    </Box>
  );
}

/**
 * ListSkeleton - Skeleton for mobile card lists
 *
 * Usage:
 *   <ListSkeleton count={5} />
 */
export function ListSkeleton({ count = 5 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton variant="text" width="50%" height={24} />
              <Skeleton variant="rounded" width={60} height={24} />
            </Box>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="40%" height={18} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

/**
 * FormSkeleton - Skeleton for form dialogs
 *
 * Usage:
 *   <FormSkeleton fields={4} />
 */
export function FormSkeleton({ fields = 4 }) {
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {Array.from({ length: fields }).map((_, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton variant="text" width="30%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="rounded" width="100%" height={40} />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Skeleton variant="rounded" width={80} height={36} />
        <Skeleton variant="rounded" width={100} height={36} />
      </Box>
    </Box>
  );
}

/**
 * PageHeaderSkeleton - Skeleton for page headers with title and filters
 */
export function PageHeaderSkeleton({ showFilters = true }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rounded" width={120} height={36} />
      </Box>
      {showFilters && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rounded" width={150} height={40} />
          <Skeleton variant="rounded" width={150} height={40} />
          <Skeleton variant="rounded" width={150} height={40} />
        </Box>
      )}
    </Box>
  );
}

const SkeletonLoaders = {
  TableSkeleton,
  CardSkeleton,
  DashboardSkeleton,
  ListSkeleton,
  FormSkeleton,
  PageHeaderSkeleton
};

export default SkeletonLoaders;
